/**
 * Pont entre l’API `widget_workspaces` et l’UI participant / aperçu admin.
 *
 * Flux typique :
 * 1. `widgetItemConfigurations[]` (clé + slideIndex + configValue) → slides normalisées ;
 * 2. affichage : IRI `/medias/{id}` → GET média → URL absolue (`resolveWidgetAssetUrl`) ;
 * 3. fond responsive : `resolveSlideBackgroundImageIri` (< 768px → mobile si présent).
 */
import type { WidgetItemConfigurationWidgetWorkspaceReadModel } from '@r/api/generated/client.schemas.ts';

/** Nombre maximal de slides par bloc (carousel). */
export const MAX_WIDGET_SLIDES = 3;

const WIDGET_ASSET_BASE_URL =
  (import.meta.env.VITE_BASE_URL as string | undefined)?.replace(/\/$/, '') ?? '';

/** Identifiants des pages éditables dans l’admin « Bloc de contenu ». */
export type ContentBlockPageId = 'home' | 'shop';

/** Indique si la clé widget cible la page Home (ex. contient `home`). */
export const isHomeWidgetKey = (key: string | undefined): boolean => (key ?? '').includes('home');

/** Indique si la clé widget cible la page Boutique (ex. `bloc_cta_shop`). */
export const isShopWidgetKey = (key: string | undefined): boolean => (key ?? '').includes('shop');

/** Associe une clé widget à une page de l’éditeur de blocs de contenu. */
export const isWidgetKeyForContentBlockPage = (
  key: string | undefined,
  pageId: ContentBlockPageId
): boolean => {
  switch (pageId) {
    case 'home':
      return isHomeWidgetKey(key);
    case 'shop':
      return isShopWidgetKey(key);
    default:
      return false;
  }
};

/** Résout un chemin média relatif en URL absolue (préfixe VITE_BASE_URL). */
export const resolveWidgetAssetUrl = (path?: string | null): string | undefined => {
  if (!path) return undefined;
  if (path.startsWith('http')) return path;
  return `${WIDGET_ASSET_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
};

/** Extrait l’identifiant d’une IRI API Platform `/medias/{id}`. */
export const getMediaIdFromIri = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  return /^\/medias\/([^/]+)$/.exec(value.trim())?.[1] ?? null;
};

/** Largeur max (px) pour utiliser `background_image_mobile` plutôt que `background_image`. */
export const WIDGET_BACKGROUND_MOBILE_MAX_WIDTH_PX = 768;

/** Données d’une slide prêtes pour `WidgetSlideCard` (clés camelCase, pas les itemKey API). */
export type WidgetSlideContent = {
  slideIndex: number;
  description?: string;
  textCta?: string;
  textColor?: string;
  backgroundImage?: string;
  backgroundImageMobile?: string;
  externLink?: string;
  filterImage?: string;
  ctaPosition?: string;
};

export type WidgetCtaPosition =
  | 'below_text'
  | 'right_of_text'
  | 'left_of_text'
  | 'aligned_with_text';

const WIDGET_ITEM_KEYS = [
  'description',
  'text_cta',
  'text_color',
  'background_image',
  'background_image_mobile',
  'extern_link',
  'filter_image',
  'cta_position'
] as const;

type WidgetItemKey = (typeof WIDGET_ITEM_KEYS)[number];

/** Nombre de slides d’un widget (1 par défaut, plafonné à MAX_WIDGET_SLIDES). */
export const getWidgetSlideCount = (
  configurations: WidgetItemConfigurationWidgetWorkspaceReadModel[]
): number => {
  if (configurations.length === 0) return 1;

  const maxSlideIndex = Math.max(...configurations.map((config) => config.slideIndex ?? 1));
  return Math.min(MAX_WIDGET_SLIDES, Math.max(1, maxSlideIndex));
};

/** Lit la configValue d’un champ donné pour une slide précise. */
const getConfigValueForSlide = (
  configurations: WidgetItemConfigurationWidgetWorkspaceReadModel[],
  slideIndex: number,
  itemKey: WidgetItemKey
): string | undefined => {
  const config = configurations.find(
    (entry) =>
      (entry.slideIndex ?? 1) === slideIndex && entry.widgetItemDefinition.itemKey === itemKey
  );

  const value = config?.configValue;
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

/**
 * Opacité de l’overlay sombre sur l’image (0–1) à partir de `filter_image` (0–100).
 * `0`, vide ou invalide → pas d’overlay (image brute).
 */
export const getWidgetImageOverlayOpacity = (filterImage?: string | null): number | undefined => {
  if (filterImage === undefined || filterImage === null) {
    return undefined;
  }
  const trimmed = filterImage.trim();
  if (trimmed.length === 0) {
    return undefined;
  }
  const percent = Math.min(100, Math.max(0, Number(trimmed)));
  if (Number.isNaN(percent) || percent <= 0) {
    return undefined;
  }
  return percent / 100;
};

/**
 * Choisit l’IRI média de fond selon la largeur viewport.
 * Sous {@link WIDGET_BACKGROUND_MOBILE_MAX_WIDTH_PX} : mobile si renseigné, sinon repli sur desktop.
 */
export const resolveSlideBackgroundImageIri = (
  slide: Pick<WidgetSlideContent, 'backgroundImage' | 'backgroundImageMobile'>,
  viewportIsMobile: boolean
): string | undefined => {
  if (viewportIsMobile && slide.backgroundImageMobile) {
    return slide.backgroundImageMobile;
  }
  return slide.backgroundImage;
};

/**
 * Agrège les champs CTA connus par slide pour l’affichage client (carousel).
 * `background_image` est une IRI média (`configValue` API), pas un chemin `/assets/…`.
 */
export const getWidgetSlidesFromConfigurations = (
  configurations: WidgetItemConfigurationWidgetWorkspaceReadModel[]
): WidgetSlideContent[] => {
  const slideCount = getWidgetSlideCount(configurations);
  const slides: WidgetSlideContent[] = [];

  for (let slideIndex = 1; slideIndex <= slideCount; slideIndex++) {
    slides.push({
      slideIndex,
      description: getConfigValueForSlide(configurations, slideIndex, 'description'),
      textCta: getConfigValueForSlide(configurations, slideIndex, 'text_cta'),
      textColor: getConfigValueForSlide(configurations, slideIndex, 'text_color'),
      backgroundImage: getConfigValueForSlide(configurations, slideIndex, 'background_image'),
      backgroundImageMobile: getConfigValueForSlide(
        configurations,
        slideIndex,
        'background_image_mobile'
      ),
      externLink: getConfigValueForSlide(configurations, slideIndex, 'extern_link'),
      filterImage: getConfigValueForSlide(configurations, slideIndex, 'filter_image'),
      ctaPosition: getConfigValueForSlide(configurations, slideIndex, 'cta_position')
    });
  }

  return slides;
};

const optionalString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value : undefined;

/** Convertit les valeurs formulaire admin (itemKey snake_case) en {@link WidgetSlideContent}. */
export const mapSlideValuesToWidgetContent = (
  slideIndex: number,
  values: Record<string, unknown>
): WidgetSlideContent => ({
  slideIndex,
  description: optionalString(values.description),
  textCta: optionalString(values.text_cta),
  textColor: optionalString(values.text_color),
  backgroundImage: optionalString(values.background_image),
  backgroundImageMobile: optionalString(values.background_image_mobile),
  externLink: optionalString(values.extern_link),
  filterImage: optionalString(values.filter_image),
  ctaPosition: optionalString(values.cta_position)
});

/**
 * Slides pour l’aperçu instantané admin à partir de l’état formulaire (non persisté).
 * Utilisé par `getEditorPreviewWidgetSlides` dans content-block-slides.ts.
 */
export const getWidgetSlidesFromSlideValues = (
  slideCount: number,
  slidesByIndex: Record<number, Record<string, unknown>>
): WidgetSlideContent[] => {
  const slides: WidgetSlideContent[] = [];
  for (let slideIndex = 1; slideIndex <= slideCount; slideIndex++) {
    slides.push(mapSlideValuesToWidgetContent(slideIndex, slidesByIndex[slideIndex] ?? {}));
  }
  return slides;
};
